import React, { useState } from "react";
import {
    FormHelperText,
    Button,
    Snackbar,
    FormControl,
    Box,
    Chip,
    Typography,
    Paper,
} from "@material-ui/core";

import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { uploadFileTypeRegister } from "app/c1utils/const";
import C1Alert from "app/c1component/C1Alert";

const useStyles = makeStyles((theme) => ({
    list: {
        display: 'grid',
        gap: theme.spacing(1.5),
    },
    itemCard: {
        border: '1px solid #e3e8f2',
        borderRadius: 18,
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    itemMeta: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'flex-start',
        minWidth: 0,
    },
    iconWrap: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: '#eef4ff',
        color: '#3C77D0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto',
    },
    docTitle: {
        fontWeight: 700,
        color: '#1f2a44',
    },
    docHelp: {
        color: '#6b7a99',
        marginTop: theme.spacing(0.4),
    },
    right: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        [theme.breakpoints.down('xs')]: {
            justifyContent: 'space-between',
        },
    },
    statusChip: {
        borderRadius: 999,
        fontWeight: 600,
        backgroundColor: '#f6f8fc',
        color: '#6b7a99',
    },
    requiredChip: {
        height: 24,
        fontWeight: 700,
        backgroundColor: '#fff4cc',
        color: '#9a7300',
        marginLeft: theme.spacing(1),
    },
    uploadButton: {
        borderRadius: 12,
        backgroundColor: '#3C77D0',
        color: '#fff',
        textTransform: 'none',
        fontWeight: 700,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#2f64b4',
            boxShadow: 'none',
        },
    },
    specText: {
        color: '#6b7a99',
        marginTop: theme.spacing(2),
        lineHeight: 1.8,
    },
}));

const MAX_FILE_SIZE = 10;

const RegisterDocs = ({
    inputData,
    isDisabled,
    errors,
}) => {
    const classes = useStyles();
    const [isSubmitSuccess, setSubmitSuccess] = useState(false);
    const [snackBarState, setSnackBarState] = useState({
        open: false,
        vertical: 'top',
        horizontal: 'center',
        msg: '',
        severity: 'success'
    });
    const [, setRefreshList] = useState(0);

    if (!inputData.accnDocs) {
        inputData.accnDocs = [];
    }

    const onLocalFileChangeHandler = (e, idx) => {
        e.preventDefault();
        const file = e.target.files[0];
        if (!file) {
            return;
        }

        const fileReader = new FileReader();
        fileReader.readAsArrayBuffer(file);
        fileReader.onload = event => {
            const uint8Array = new Uint8Array(event.target.result);
            if (!validteFile(uint8Array, file.name)) {
                return;
            }

            const buff = new Buffer.from(uint8Array);
            const base64data = buff.toString('base64');
            const tmpAccnDoc = { ...inputData.accnDocs[idx], attName: file.name, attData: base64data };
            inputData.accnDocs[idx] = tmpAccnDoc;
            setRefreshList(prev => prev + 1);
        };
    };

    const validteFile = (uint8Array, fileName) => {
        if (!uint8Array || uint8Array.length === 0) {
            setSubmitSuccess(true);
            setSnackBarState(sb => ({ ...sb, open: true, msg: 'File size is too small', severity: 'error' }));
            return false;
        }
        if (uint8Array.length > MAX_FILE_SIZE * 1024 * 1024) {
            setSubmitSuccess(true);
            setSnackBarState(sb => ({ ...sb, open: true, msg: `File size can't greater than ${MAX_FILE_SIZE}MB.`, severity: 'error' }));
            return false;
        }
        if (fileName.length > 128) {
            setSubmitSuccess(true);
            setSnackBarState(sb => ({ ...sb, open: true, msg: 'File name shoule less than 128 characters.', severity: 'error' }));
            return false;
        }
        return true;
    };

    const handleClose = () => {
        setSnackBarState({ ...snackBarState, open: false });
    };

    const snackBar = isSubmitSuccess ? (
        <Snackbar
            anchorOrigin={{ vertical: snackBarState.vertical, horizontal: snackBarState.horizontal }}
            open={snackBarState.open}
            onClose={handleClose}
            autoHideDuration={3000}
            key={snackBarState.vertical + snackBarState.horizontal}
        >
            <C1Alert onClose={handleClose} severity={snackBarState.severity}>
                {snackBarState.msg}
            </C1Alert>
        </Snackbar>
    ) : null;

    return (
        <React.Fragment>
            {snackBar}

            <div className={classes.list}>
                {inputData.accnDocs && inputData.accnDocs.map((row, index) => {
                    const isUploaded = Boolean(row.attName && row.attData);
                    return (
                        <Paper key={row.attType || index} elevation={0} className={classes.itemCard}>
                            <div className={classes.itemMeta}>
                                <div className={classes.iconWrap}>
                                    <DescriptionOutlinedIcon />
                                </div>
                                <div>
                                    <Typography className={classes.docTitle}>
                                        {row.attDesc}
                                        <Chip label="Required" size="small" className={classes.requiredChip} />
                                    </Typography>
                                    <Typography variant="body2" className={classes.docHelp}>
                                        PDF, JPG, or PNG. Max 10 MB.
                                    </Typography>
                                    <FormHelperText error>{errors[`accnDocs.${row.attType}`] || ''}</FormHelperText>
                                </div>
                            </div>

                            <div className={classes.right}>
                                <Chip
                                    label={isUploaded ? row.attName : 'Not uploaded'}
                                    className={classes.statusChip}
                                />
                                <FormControl component="fieldset" error={errors[`accnDocs.${row.attType}`] !== undefined}>
                                    <label htmlFor={`upload-multiple-file-${row.attType}`}>
                                        <Button
                                            type="button"
                                            disabled={isDisabled}
                                            color="primary"
                                            component="span"
                                            variant="contained"
                                            className={classes.uploadButton}
                                            startIcon={<CloudUploadIcon />}
                                        >
                                            Upload file
                                        </Button>
                                    </label>
                                    <input
                                        className="hidden"
                                        onChange={(e) => onLocalFileChangeHandler(e, index)}
                                        id={`upload-multiple-file-${row.attType}`}
                                        disabled={isDisabled}
                                        type="file"
                                        single="true"
                                        accept={uploadFileTypeRegister}
                                    />
                                </FormControl>
                            </div>
                        </Paper>
                    );
                })}
            </div>

            <Box className={classes.specText}>
                <Typography variant="body2">Document specification</Typography>
                <Typography variant="body2">Must be .pdf, .jpg, .jpeg, or .png</Typography>
                <Typography variant="body2">Max file size 10 MB</Typography>
                <Typography variant="body2">File name cannot be more than 128 characters</Typography>
            </Box>
        </React.Fragment>
    );
};

export default RegisterDocs;
